"use client";

import React, {
  createContext,
  useContext,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";

import { Transition } from "@headlessui/react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";

import { Toast } from "@/components/flowbite/toasts";

interface ToastData {
  id: string;
  message?: string | ReactNode;
  description?: string | ReactNode;
  type: "success" | "warning" | "error";
  duration?: number;
  onClose?: () => void;
  onActionMore?: () => void;
  onActionMoreCallback?: () => void | Promise<void>;
  actionText?: string;
  icon?: React.ReactNode;
  visible: boolean;
  postActionText?: string;
  postActionIcon?: React.ReactNode;
}

export interface ToastContextType {
  showToast: (data: Omit<ToastData, "id" | "visible">) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

type ToastAction =
  | { type: "ADD_TOAST"; toast: ToastData }
  | { type: "REMOVE_TOAST"; id: string }
  | { type: "HIDE_TOAST"; id: string }
  | { type: "UPDATE_TOAST"; id: string; data: Partial<ToastData> };

const toastReducer = (state: ToastData[], action: ToastAction): ToastData[] => {
  switch (action.type) {
    case "ADD_TOAST":
      return [...state, action.toast];
    case "REMOVE_TOAST":
      return state.filter((toast) => toast.id !== action.id);
    case "HIDE_TOAST":
      return state.map((toast) => (toast.id === action.id ? { ...toast, visible: false } : toast));
    case "UPDATE_TOAST":
      return state.map((toast) => (toast.id === action.id ? { ...toast, ...action.data } : toast));
    default:
      return state;
  }
};

const TOAST_PORTAL_CLASS =
  "fixed inset-x-4 top-[4.5rem] z-toast w-11/12 l:inset-x-auto l:right-5 l:top-[4.75rem] l:w-full l:max-w-[371px]";

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [toasts, dispatch] = useReducer(toastReducer, []);

  const showToast = useCallback((data: Omit<ToastData, "id" | "visible">) => {
    const id = uuidv4();
    const toast: ToastData = { id, visible: true, ...data };
    dispatch({ type: "ADD_TOAST", toast });

    const duration = data.duration ?? 5000;
    if (duration !== 0) {
      setTimeout(() => {
        dispatch({ type: "HIDE_TOAST", id });
        data.onClose?.();
      }, duration);
    }
  }, []);

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  const toastPortal =
    typeof document !== "undefined"
      ? createPortal(
        toasts.map((toast) => (
          <Transition
            key={toast.id}
            appear
            afterLeave={() => dispatch({ type: "REMOVE_TOAST", id: toast.id })}
            as="div"
            className={TOAST_PORTAL_CLASS}
            enter="transition-opacity duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={toast.visible}
          >
            <Toast
              description={toast.description}
              message={toast.message}
              type={toast.type}
              onClose={() => {
                dispatch({ type: "HIDE_TOAST", id: toast.id });
                toast.onClose?.();
              }}
            />
          </Transition>
        )),
        document.body,
      )
      : null;

  return (
    <ToastContext.Provider value={contextValue}>
      {toastPortal}
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
